import { Icon } from './Icon';
import { CountUp } from './CountUp';
import { useInView } from '../hooks';

interface Row {
  icon: 'refresh-cw' | 'shield' | 'headphones';
  problemStat: string; problemTitle: string; problemNote: string;
  solutionStat: string; solutionTitle: string; solutionNote: string;
}

const ROWS: Row[] = [
  {
    icon: 'refresh-cw',
    problemStat: '68%',  problemTitle: 'Abandoned carts die in the inbox', problemNote: 'never recovered by email',
    solutionStat: '23%', solutionTitle: 'AI calls within minutes, while intent is hot', solutionNote: 'recovered by voice',
  },
  {
    icon: 'shield',
    problemStat: '28%', problemTitle: 'COD orders ship without confirmation', problemNote: 'return-to-origin losses',
    solutionStat: '9%', solutionTitle: 'Every COD order verified before dispatch', solutionNote: 'RTO after calls',
  },
  {
    icon: 'headphones',
    problemStat: '40%',  problemTitle: 'Support calls go unanswered after hours', problemNote: 'calls missed at night',
    solutionStat: '1.2s',solutionTitle: 'AI answers instantly, 24/7, in any language', solutionNote: 'average pickup',
  },
];

export function ProblemSolution() {
  const [ref, inView] = useInView<HTMLDivElement>(0.25);

  return (
    <section className="py-24 ps-section" id="problem" ref={ref}>
      <div className="mx-auto w-full max-w-[1180px] px-7">
        <div className="reveal max-w-[680px] mx-auto mb-14 text-center">
          <span className="lp-eyebrow" style={{ color: 'var(--danger)', background: 'var(--danger-bg)', borderColor: 'transparent' }}>
            <Icon name="alert-triangle" size={13} /> The problem
          </span>
          <h2 className="text-[clamp(28px,3.6vw,42px)] font-bold tracking-[-0.03em] leading-[1.1] mt-4">Your store is leaking revenue, silently</h2>
          <p className="text-[clamp(16px,1.5vw,19px)] text-[var(--text-2)] leading-[1.6] mt-3.5">
            Email opens are falling and SMS gets ignored. The moments that actually drive revenue need a conversation, not another notification.
          </p>
        </div>

        <div className={`ps-leak ${inView ? 'in' : ''} reveal`}>
          <div className="ps-leak-side problem">
            <span className="ps-leak-label">
              <span className="dot" style={{ background: 'var(--danger)' }}></span> The old way
            </span>
            <div className="ps-leak-big">
              <CountUp to={73} trigger={inView} suffix="%" />
            </div>
            <div className="ps-leak-sub">of recovery opportunities lost to unopened email &amp; SMS</div>
            <div className="ps-drops" aria-hidden>
              {Array.from({ length: 7 }).map((_, i) => (
                <span
                  key={i}
                  className="ps-drop"
                  style={{ left: `${8 + i * 13}%`, animationDelay: `${i * 0.4}s` }}
                />
              ))}
            </div>
          </div>
          <div className="ps-leak-arrow">
            <div className="ps-leak-arrow-circle"><Icon name="arrow-right" size={22} color="#fff" /></div>
            <span className="ps-leak-arrow-text">CallXcel</span>
          </div>
          <div className="ps-leak-side solution">
            <span className="ps-leak-label">
              <span className="dot" style={{ background: 'var(--success)' }}></span> With CallXcel
            </span>
            <div className="ps-leak-big accent">
              <CountUp to={4.2} trigger={inView} prefix="₹" suffix="L" decimals={1} />
            </div>
            <div className="ps-leak-sub">recovered every week through real conversations</div>
            <div className="ps-rise">
              {[34, 48, 40, 62, 75, 88].map((h, i) => (
                <span key={i} className="ps-rise-bar" style={{ '--h': `${h}%`, animationDelay: `${0.3 + i * 0.09}s` } as React.CSSProperties} />
              ))}
            </div>
          </div>
        </div>

        <div className="ps-rows">
          {ROWS.map((r, i) => (
            <div
              className={`ps-row ${inView ? 'in' : ''}`}
              key={i}
              style={{ animationDelay: `${0.2 + i * 0.12}s` }}
            >
              <div className="ps-card problem">
                <div className="ps-card-icon problem"><Icon name="x" size={16} /></div>
                <div>
                  <div className="ps-card-stat problem">{r.problemStat}</div>
                  <div className="ps-card-title">{r.problemTitle}</div>
                  <div className="ps-card-note">{r.problemNote}</div>
                </div>
              </div>
              <div className="ps-row-arrow"><Icon name={r.icon} size={18} /></div>
              <div className="ps-card solution">
                <div className="ps-card-icon solution"><Icon name="check" size={16} /></div>
                <div>
                  <div className="ps-card-stat solution">{r.solutionStat}</div>
                  <div className="ps-card-title">{r.solutionTitle}</div>
                  <div className="ps-card-note">{r.solutionNote}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
